import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CpuChipIcon,
  CircleStackIcon,
  GlobeAltIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  BoltIcon,
  ServerStackIcon,
  CommandLineIcon,
  ArrowsRightLeftIcon,
  ChartBarSquareIcon,
  TableCellsIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import LogoPixel from '../assets/logo-pixel.png';

/* ─── Data Constants ─── */

const stats = [
  { label: 'Analyzers', value: '20' },
  { label: 'Tests', value: '500+' },
  { label: 'Commands', value: '13' },
  { label: 'AI Targets', value: '7' },
];

const comparisonMetrics = [
  {
    label: 'Context tokens',
    without: '~17,800',
    withUiq: '~325',
    improvement: '98%',
    description: 'Tokens consumed to understand the codebase',
  },
  {
    label: 'Exploration steps',
    without: '12 calls',
    withUiq: '1 read',
    improvement: '12x',
    description: 'Tool calls needed for full project context',
  },
  {
    label: 'Time to context',
    without: '~36s',
    withUiq: '~4s',
    improvement: '9x',
    description: 'Seconds until AI understands your project',
  },
  {
    label: 'Component resolution',
    without: '0/4',
    withUiq: '4/4',
    improvement: '100%',
    description: 'Correct file as top-1 result for task queries',
  },
  {
    label: 'Dependencies mapped',
    without: '0',
    withUiq: '10',
    improvement: '10',
    description: 'Import and render relationships detected',
  },
  {
    label: 'Architecture insights',
    without: '0',
    withUiq: '5',
    improvement: '5',
    description: 'Naming issues, missing barrels, smells found',
  },
];

const comparisonNote = {
  project: 'frontendv1-test',
  files: 11,
  totalChars: '71,322',
  claudeMdChars: '1,299',
  compressionRatio: '55x',
};

const steps = [
  {
    step: '01',
    title: 'Install',
    command: 'npm install -g uiquarter',
    description: 'One command. No configuration needed.',
  },
  {
    step: '02',
    title: 'Analyze',
    command: 'uiquarter init',
    description: '20 analyzers scan your entire codebase in seconds.',
  },
  {
    step: '03',
    title: 'Generate',
    command: 'uiquarter generate --target all',
    description: 'AI context files for every major coding assistant.',
  },
];

const analyzerCategories = [
  {
    title: 'Core',
    count: 7,
    icon: CpuChipIcon,
    items: ['Import', 'Component', 'Styling', 'FileStructure', 'Dependency', 'Structure', 'UX'],
  },
  {
    title: 'Framework',
    count: 7,
    icon: GlobeAltIcon,
    items: ['Next.js', 'Nuxt', 'SvelteKit', 'Angular', 'Solid', 'Lit', 'Qwik'],
  },
  {
    title: 'Backend',
    count: 4,
    icon: CircleStackIcon,
    items: ['API Routes', 'Database', 'Auth', 'Env Config'],
  },
  {
    title: 'Quality',
    count: 2,
    icon: ShieldCheckIcon,
    items: ['Coverage', 'Performance'],
  },
];

const mcpTools = [
  { name: 'uiq_scope_context', desc: 'Task-scoped architectural context under a token budget' },
  { name: 'uiq_query_component', desc: 'Full component details with deps, dependents, and types' },
  { name: 'uiq_resolve_task', desc: 'Free-form task to relevant files with scored matches' },
  { name: 'uiq_find_insights', desc: 'Architectural issues filtered by severity and type' },
];

const conventionRules = [
  'file-naming', 'dir-naming', 'component-naming', 'barrel-exports',
  'circular-deps', 'max-chain-depth', 'single-styling',
  'min-accessibility', 'max-nesting-depth', 'test-colocation',
];

const driftPanels = [
  {
    title: 'Snapshot Comparison',
    cmd: 'uiquarter drift',
    lines: [
      'Patterns: 53 \u2192 58 (+5)',
      'Insights: 5 \u2192 7 (+2 new)',
      'Edges:    10 \u2192 12 (+2)',
    ],
  },
  {
    title: 'Branch Diff',
    cmd: 'uiquarter drift --compare main',
    lines: [
      'Hash changed: true',
      'Added: AuthGuard, RoleCheck',
      'New insight: hub-component',
    ],
  },
  {
    title: 'CI Pipeline',
    cmd: 'uiquarter ci --fail-on warning',
    lines: [
      'New issues:  2',
      'Resolved:    1',
      'Exit code:   1 (FAIL)',
    ],
  },
];

const aiTargets = [
  {
    name: 'Claude',
    file: 'CLAUDE.md',
    icon: (
      <svg viewBox="0 0 16 16" className="w-7 h-7" fill="#D97757">
        <path d="m3.127 10.604 3.135-1.76.053-.153-.053-.085H6.11l-.525-.032-1.791-.048-1.554-.065-1.505-.08-.38-.081L0 7.832l.036-.234.32-.214.455.04 1.009.069 1.513.105 1.097.064 1.626.17h.259l.036-.105-.089-.065-.068-.064-1.566-1.062-1.695-1.121-.887-.646-.48-.327-.243-.306-.104-.67.435-.48.585.04.15.04.593.456 1.267.981 1.654 1.218.242.202.097-.068.012-.049-.109-.181-.9-1.626-.96-1.655-.428-.686-.113-.411a2 2 0 0 1-.068-.484l.496-.674L4.446 0l.662.089.279.242.411.94.666 1.48 1.033 2.014.302.597.162.553.06.17h.105v-.097l.085-1.134.157-1.392.154-1.792.052-.504.25-.605.497-.327.387.186.319.456-.045.294-.19 1.23-.37 1.93-.243 1.29h.142l.161-.16.654-.868 1.097-1.372.484-.545.565-.601.363-.287h.686l.505.751-.226.775-.707.895-.585.759-.839 1.13-.524.904.048.072.125-.012 1.897-.403 1.024-.186 1.223-.21.553.258.06.263-.218.536-1.307.323-1.533.307-2.284.54-.028.02.032.04 1.029.098.44.024h1.077l2.005.15.525.346.315.424-.053.323-.807.411-3.631-.863-.872-.218h-.12v.073l.726.71 1.331 1.202 1.667 1.55.084.383-.214.302-.226-.032-1.464-1.101-.565-.497-1.28-1.077h-.084v.113l.295.432 1.557 2.34.08.718-.112.234-.404.141-.444-.08-.911-1.28-.94-1.44-.759-1.291-.093.053-.448 4.821-.21.246-.484.186-.403-.307-.214-.496.214-.98.258-1.28.21-1.016.19-1.263.112-.42-.008-.028-.092.012-.953 1.307-1.448 1.957-1.146 1.227-.274.109-.477-.247.045-.44.266-.39 1.586-2.018.956-1.25.617-.723-.004-.105h-.036l-4.212 2.736-.75.096-.324-.302.04-.496.154-.162 1.267-.871z"/>
      </svg>
    ),
  },
  {
    name: 'Codex',
    file: 'AGENTS.md',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" fill="#fff"/>
      </svg>
    ),
  },
  {
    name: 'Cursor',
    file: '.cursorrules',
    icon: (
      <svg viewBox="0 0 400 400" className="w-7 h-7" fill="none">
        <path fill="#fff" d="M315.156 133.518L199.233 200.445C198.741 199.588 198.023 198.86 197.134 198.345L86.049 134.209C84.731 133.453 85.272 131.445 86.788 131.445H311.566C313.162 131.445 314.459 132.307 315.155 133.518Z"/>
        <path fill="#999" d="M200.004 200L323.821 271.486C323.061 272.804 321.957 273.929 320.591 274.716L204.863 341.531C201.858 343.266 198.151 343.266 195.146 341.531L79.417 274.716C78.052 273.929 76.948 272.804 76.188 271.486L200.004 200Z"/>
        <path fill="#666" d="M200.005 57.168V200L76.188 271.486C75.427 270.168 75.004 268.647 75.004 267.072V132.928C75.004 129.774 76.686 126.865 79.418 125.285L195.141 58.47C196.646 57.602 198.323 57.168 199.999 57.168H200.005Z"/>
        <path fill="#444" d="M323.815 128.515C323.055 127.197 321.951 126.072 320.586 125.285L204.857 58.47C203.357 57.602 201.68 57.168 200.004 57.168V200L323.821 271.486C324.581 270.168 325.005 268.647 325.005 267.072V132.928C325.005 131.348 324.587 129.838 323.821 128.515H323.815Z"/>
      </svg>
    ),
  },
  {
    name: 'Windsurf',
    file: '.windsurfrules',
    icon: (
      <svg viewBox="0 0 1024 1024" className="w-7 h-7" fill="none">
        <path d="M897.246 286.869H889.819C850.735 286.808 819.017 318.46 819.017 357.539V515.589C819.017 547.15 792.93 572.716 761.882 572.716C743.436 572.716 725.02 563.433 714.093 547.85L552.673 317.304C539.28 298.16 517.486 286.747 493.895 286.747C457.094 286.747 423.976 318.034 423.976 356.657V515.619C423.976 547.181 398.103 572.746 366.842 572.746C348.335 572.746 329.949 563.463 319.021 547.881L138.395 289.882C134.316 284.038 125.154 286.93 125.154 294.052V431.892C125.154 438.862 127.285 445.619 131.272 451.34L309.037 705.2C319.539 720.204 335.033 731.344 352.9 735.392C397.616 745.557 438.77 711.135 438.77 667.278V508.406C438.77 476.845 464.339 451.279 495.904 451.279H495.995C515.02 451.279 532.857 460.562 543.785 476.145L705.235 706.661C718.659 725.835 739.327 737.218 763.983 737.218C801.606 737.218 833.841 705.9 833.841 667.308V508.376C833.841 476.815 859.41 451.249 890.975 451.249H897.276C901.233 451.249 904.43 448.053 904.43 444.097V294.021C904.43 290.065 901.233 286.869 897.276 286.869H897.246Z" fill="#00C8FF"/>
      </svg>
    ),
  },
  {
    name: 'Cline',
    file: '.clinerules',
    icon: (
      <svg viewBox="0 0 92 96" className="w-7 h-7" fill="#E8A845">
        <path d="M65.449 16.3C76.337 16.3 85.164 25.165 85.164 36.1v6.6l5.739 11.465a4.5 4.5 0 0 1-.007 3.595L85.164 69.1v6.6c0 10.936-8.826 19.8-19.715 19.8H26.021c-10.888 0-19.714-8.864-19.714-19.8v-6.6L.449 57.795a4.5 4.5 0 0 1-.008-3.667L6.307 42.7v-6.6c0-10.935 8.826-19.8 19.714-19.8h39.428z" fillRule="nonzero"/>
        <circle cx="45.735" cy="11" r="11"/>
        <rect x="27" y="40.5" width="13" height="30" rx="6.5" fill="#1a1a2e"/>
        <rect x="51" y="40.5" width="13" height="30" rx="6.5" fill="#1a1a2e"/>
      </svg>
    ),
  },
  {
    name: 'Copilot',
    file: 'copilot-instructions.md',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7">
        <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2Z" fill="#fff"/>
      </svg>
    ),
  },
  {
    name: 'Aider',
    file: 'CONVENTIONS.md',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#14b014" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 17 10 11 4 5"/>
        <line x1="12" y1="19" x2="20" y2="19"/>
      </svg>
    ),
  },
];

const commands = [
  { cmd: 'uiquarter init', desc: 'Run full analysis pipeline' },
  { cmd: 'uiquarter query stats', desc: 'Project statistics' },
  { cmd: 'uiquarter query hubs', desc: 'Find hub components' },
  { cmd: 'uiquarter resolve "add modal"', desc: 'Match task to patterns' },
  { cmd: 'uiquarter generate --target all', desc: 'Generate AI context files' },
  { cmd: 'uiquarter drift --compare main', desc: 'Compare branches' },
  { cmd: 'uiquarter ci --fail-on warning', desc: 'CI regression check' },
  { cmd: 'uiquarter lint', desc: 'Convention enforcement' },
  { cmd: 'uiquarter report --open', desc: 'HTML dashboard' },
  { cmd: 'uiquarter serve', desc: 'MCP server for AI tools' },
];

const frameworks = [
  { name: 'React', color: '#61DAFB', desc: 'Component tree, hooks, context detection', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
  { name: 'Vue', color: '#4FC08D', desc: 'SFC parsing, Composition API, Pinia stores', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg' },
  { name: 'Svelte', color: '#FF3E00', desc: 'Runes, stores, reactive declarations', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/svelte/svelte-original.svg' },
  { name: 'Angular', color: '#DD0031', desc: 'Modules, services, dependency injection', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angular/angular-original.svg' },
  { name: 'Next.js', color: '#ffffff', desc: 'App Router, Server Components, API routes', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg' },
  { name: 'Nuxt', color: '#00DC82', desc: 'Auto-imports, Nitro server, composables', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nuxtjs/nuxtjs-original.svg' },
  { name: 'SvelteKit', color: '#FF3E00', desc: 'Load functions, layouts, form actions', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/svelte/svelte-original.svg' },
  { name: 'Solid', color: '#2C4F7C', desc: 'Signals, createResource, JSX components', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/solidjs/solidjs-original.svg' },
  { name: 'Lit', color: '#325CFF', desc: 'Web Components, Shadow DOM, decorators', logo: 'https://cdn.simpleicons.org/lit/325CFF' },
  { name: 'Qwik', color: '#AC7EF4', desc: 'Resumability, lazy loading, QwikCity routes', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/qwik/qwik-original.svg' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── Gradient Border Box ─── */
const GradientBox = ({ children, className = '' }) => (
  <div className={`relative rounded-2xl p-px bg-gradient-to-br from-[#FF3366]/40 via-[#FF6B4D]/20 to-[#FF3366]/10 ${className}`}>
    <div className="rounded-2xl bg-[#0A0A10] p-8 md:p-12">
      {children}
    </div>
  </div>
);

/* ─── Reusable Terminal Shell ─── */
const TerminalBlock = ({ title, children, className = '' }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#0D0D12] overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#0A0A0F]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
      {title && <span className="ml-2 text-xs text-[#B4A5BD] font-mono">{title}</span>}
    </div>
    <div className="p-5 font-mono text-sm leading-relaxed">{children}</div>
  </div>
);

/* ─── Page ─── */
export default function Home() {
  return (
    <div className="relative overflow-hidden">
      <SEO
        title="UIQuarter - Static Analysis for UI Codebases"
        description="Analyze component patterns, dependency graphs, and architecture insights. Generate AI-optimized context files for Claude, Cursor, Copilot, and more."
      />

      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF3366]/5 rounded-full blur-3xl" />
      <div className="absolute top-40 right-1/4 w-96 h-96 bg-[#FF6B4D]/5 rounded-full blur-3xl" />

      {/* ── 1. Hero ── */}
      <section className="relative pt-20 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF3366]/30 bg-[#FF3366]/5 mb-6">
              <BoltIcon className="w-4 h-4 text-[#FF3366]" />
              <span className="text-sm text-[#B4A5BD]">v0.1.0 -- 20 analyzers, 500+ tests</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Your AI doesn't know your{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                codebase. Fix that.
              </span>
            </h1>
            <p className="text-xl text-[#B4A5BD] max-w-3xl mx-auto mb-10">
              Extract component patterns, map dependency graphs, detect architectural insights,
              and generate AI-optimized context files -- all from one CLI command.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://github.com/fuatkeles/uiquarter"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white font-semibold hover:opacity-90 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                GitHub
              </a>
              <Link
                to="/docs"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl border border-white/10 text-white font-semibold hover:bg-white/5 transition-all duration-300"
              >
                <DocumentTextIcon className="w-5 h-5" />
                Documentation
              </Link>
            </div>
            <div className="mt-8 inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#FF3366]/60 to-[#FF6B4D]/30 p-px">
              <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-[#0D0D12] font-mono text-sm">
              <span className="text-[#FF3366]">$</span>
              <span className="text-white">npm install -g uiquarter</span>
              <button
                onClick={() => navigator.clipboard.writeText('npm install -g uiquarter')}
                className="ml-2 text-[#B4A5BD] hover:text-white transition-colors"
                title="Copy"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              </button>
              </div>
            </div>
          </motion.div>

          {/* Terminal Preview with Logo */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="max-w-3xl mx-auto"
          >
            <div className="rounded-2xl border border-white/10 bg-[#0D0D12] overflow-hidden shadow-2xl shadow-[#FF3366]/5">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#0A0A0F]">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <span className="ml-2 text-xs text-[#B4A5BD] font-mono">terminal</span>
              </div>
              <div className="p-6 font-mono text-sm leading-relaxed">
                {/* Banner line with logo */}
                <div className="text-[#B4A5BD]">
                  <span className="text-[#FF3366]">$</span> uiquarter init
                </div>
                <div className="mt-4 flex items-center gap-6 pb-4 border-b border-white/5">
                  <img src={LogoPixel} alt="UIQuarter" className="w-36 h-36" style={{ imageRendering: 'pixelated' }} />
                  <div>
                    <div className="text-white font-bold text-lg">UIQuarter <span className="text-[#B4A5BD] font-normal text-sm">v0.1.0</span></div>
                    <div className="text-[#B4A5BD] text-sm">Static analysis for UI codebases</div>
                    <div className="text-[#B4A5BD] text-sm">20 analyzers · 13 commands · 7 AI targets</div>
                  </div>
                </div>
                <div className="mt-4 space-y-1">
                  <div><span className="text-[#B4A5BD]">Discovered</span> <span className="text-white">100 files</span> <span className="text-[#B4A5BD]">(23ms)</span></div>
                  <div><span className="text-[#B4A5BD]">Analyzed with</span> <span className="text-[#FF6B4D]">20 analyzers</span> <span className="text-[#B4A5BD]">(60ms)</span></div>
                  <div className="mt-2"><span className="text-[#B4A5BD]">Built intelligence index:</span> <span className="text-[#FF3366]">53 patterns</span>, <span className="text-[#FF6B4D]">10 edges</span>, <span className="text-white">25 files</span></div>
                  <div><span className="text-[#B4A5BD]">Generated</span> <span className="text-white">5 insights</span> <span className="text-[#B4A5BD]">(68ms)</span></div>
                  <div className="mt-2 text-green-400">Done. <span className="text-[#B4A5BD]">(181ms)</span></div>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-[#B4A5BD] mt-4">
              Real output from analyzing a production React application
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── AI Targets Marquee ── */}
      <div className="py-6 border-y border-white/5 max-w-6xl mx-auto overflow-hidden relative" style={{ maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)' }}>
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
        <div className="flex" style={{ animation: 'marquee 25s linear infinite', width: 'max-content' }}>
          {[...aiTargets, ...aiTargets, ...aiTargets, ...aiTargets].map((t, i) => (
            <div key={i} className="flex items-center gap-3 px-8 whitespace-nowrap">
              {t.icon}
              <div>
                <div className="text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-[#B4A5BD] font-mono">{t.file}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 2. Comparison Table (moved up) ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-6">
            <h2 className="text-4xl font-bold mb-4">
              Claude Code <span className="text-[#B4A5BD] font-normal">vs</span>{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">Claude Code + UIQuarter</span>
            </h2>
            <p className="text-[#B4A5BD] text-lg max-w-2xl mx-auto">
              Tested on a real React + Tailwind app -- {comparisonNote.files} key files, {comparisonNote.totalChars} chars of source code.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-6 text-center">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/[0.02] text-xs text-[#B4A5BD]">
              <span>Source: {comparisonNote.totalChars} chars</span>
              <span className="w-1 h-1 rounded-full bg-[#FF3366]/50" />
              <span>CLAUDE.md: {comparisonNote.claudeMdChars} chars</span>
              <span className="w-1 h-1 rounded-full bg-[#FF3366]/50" />
              <span>Compression: {comparisonNote.compressionRatio}</span>
            </div>
          </motion.div>

          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-4 gap-0 bg-[#0D0D12] border-b border-white/5">
              <div className="p-4 text-sm font-semibold text-[#B4A5BD]">Metric</div>
              <div className="p-4 text-sm font-semibold text-[#B4A5BD] text-center">Without UIQuarter</div>
              <div className="p-4 text-sm font-semibold text-center bg-gradient-to-r from-[#FF3366]/10 to-[#FF6B4D]/10">
                <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent font-bold">With UIQuarter</span>
              </div>
              <div className="p-4 text-sm font-semibold text-[#B4A5BD] text-center">Gain</div>
            </div>
            {comparisonMetrics.map((metric, i) => (
              <motion.div
                key={metric.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                className={`grid grid-cols-4 gap-0 border-b border-white/5 last:border-b-0 ${i % 2 === 0 ? 'bg-white/[0.01]' : 'bg-transparent'}`}
              >
                <div className="p-4">
                  <div className="text-sm font-medium text-white">{metric.label}</div>
                  <div className="text-xs text-[#B4A5BD] mt-0.5">{metric.description}</div>
                </div>
                <div className="p-4 flex items-center justify-center">
                  <span className="font-mono text-sm text-red-400/80">{metric.without}</span>
                </div>
                <div className="p-4 flex items-center justify-center bg-gradient-to-r from-[#FF3366]/5 to-[#FF6B4D]/5">
                  <span className="font-mono text-sm text-green-400">{metric.withUiq}</span>
                </div>
                <div className="p-4 flex items-center justify-center">
                  <span className="inline-flex px-3 py-1 rounded-full bg-[#FF3366]/10 text-[#FF3366] text-xs font-bold font-mono">
                    {metric.improvement}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Methodology note */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="mt-6 p-4 rounded-xl border border-white/5 bg-white/[0.01]">
            <p className="text-xs text-[#B4A5BD] leading-relaxed">
              <span className="text-white font-semibold">Methodology:</span>{' '}
              "Without UIQuarter" simulates a typical AI assistant exploration -- 1 glob, 2 greps, 8 file reads to understand project structure.
              "With UIQuarter" reads the generated CLAUDE.md (1,299 chars).
              Component resolution tested with 4 natural-language queries ("add navbar link", "fix footer layout", "update seo meta tags", "change home page hero").
              UIQuarter returned the correct file as top-1 in all 4 cases. Grep-based search returned 2-5 results per query with the target file buried in noise.
              All numbers measured on this project's actual codebase.
            </p>
          </motion.div>

          {/* Side-by-side terminals */}
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="text-sm text-[#B4A5BD] mb-3 font-semibold">Without UIQuarter -- 12 tool calls</div>
              <div className="rounded-xl border border-white/10 bg-[#0D0D12] p-4 font-mono text-xs space-y-1.5 text-[#B4A5BD] h-full">
                <div><span className="text-[#FF3366]">$</span> glob **/*.js</div>
                <div><span className="text-[#FF3366]">$</span> read package.json <span className="text-white/30">1,430 chars</span></div>
                <div><span className="text-[#FF3366]">$</span> read src/App.js <span className="text-white/30">839 chars</span></div>
                <div><span className="text-[#FF3366]">$</span> read src/components/Navbar.js <span className="text-white/30">8,526 chars</span></div>
                <div><span className="text-[#FF3366]">$</span> read src/pages/Home.js <span className="text-white/30">31,643 chars</span></div>
                <div><span className="text-[#FF3366]">$</span> read src/index.css <span className="text-white/30">6,605 chars</span></div>
                <div><span className="text-[#FF3366]">$</span> read src/components/Footer.js <span className="text-white/30">2,517 chars</span></div>
                <div><span className="text-[#FF3366]">$</span> read src/components/SEO.js <span className="text-white/30">4,527 chars</span></div>
                <div className="text-red-400/60">... 4 more reads</div>
                <div className="text-white/30 mt-2">~71,322 chars | ~17,800 tokens | ~36s</div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="text-sm mb-3 font-semibold">
                <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">With UIQuarter -- 1 read</span>
              </div>
              <div className="rounded-xl border border-[#FF3366]/20 bg-[#0D0D12] p-4 font-mono text-xs space-y-1.5 h-full">
                <div><span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter init</span></div>
                <div className="text-[#B4A5BD]">  53 patterns, 10 edges, 5 insights</div>
                <div className="text-green-400">  Done. (685ms)</div>
                <div className="mt-3"><span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter generate --target claude</span></div>
                <div className="text-[#B4A5BD]">  Generated: CLAUDE.md (1,299 chars)</div>
                <div className="mt-3"><span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter resolve "add navbar link"</span></div>
                <div className="text-[#B4A5BD]">  Navbar.js (score: 1.00)</div>
                <div className="mt-3"><span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter resolve "fix footer layout"</span></div>
                <div className="text-[#B4A5BD]">  Footer.js (score: 1.00)</div>
                <div className="text-white/30 mt-4">~1,299 chars | ~325 tokens | ~4s</div>
              </div>
            </motion.div>
          </div>
        </GradientBox>
      </section>

      {/* ── 3. Stats Bar ── */}
      <section className="py-8 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                className="text-center"
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-[#B4A5BD] mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. How It Works ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Three steps. Zero config.</h2>
            <p className="text-[#B4A5BD] text-lg">Get started in under a minute.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <motion.div key={step.step} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants} className="relative">
                <div className="text-6xl font-bold text-[#FF3366]/10 mb-4">{step.step}</div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <div className="rounded-lg bg-[#0D0D12] border border-white/5 p-4 font-mono text-sm mb-3">
                  <span className="text-[#FF3366]">$</span> <span className="text-white">{step.command}</span>
                </div>
                <p className="text-[#B4A5BD] text-sm">{step.description}</p>
                {i < steps.length - 1 && (
                  <ArrowRightIcon className="hidden md:block absolute top-1/2 -right-6 w-5 h-5 text-[#FF3366]/30" />
                )}
              </motion.div>
            ))}
          </div>
        </GradientBox>
      </section>

      {/* ── 5. 20 Deep Analyzers ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            {/* Left: text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-[55%]"
            >
              <div className="flex items-center gap-3 mb-4">
                <CpuChipIcon className="w-7 h-7 text-[#FF3366]" />
                <h2 className="text-3xl font-bold">20 Deep Analyzers</h2>
              </div>
              <p className="text-[#B4A5BD] mb-8 max-w-lg">
                Multi-framework pattern detection across your entire stack. Components, styling, accessibility, backend routes, database models -- nothing is missed.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {analyzerCategories.map((cat) => (
                  <div key={cat.title}>
                    <div className="flex items-center gap-2 mb-3">
                      <cat.icon className="w-5 h-5 text-[#FF3366]" />
                      <span className="font-semibold">{cat.title}</span>
                      <span className="ml-auto text-xs font-mono px-2 py-0.5 rounded-full bg-[#FF3366]/10 text-[#FF3366]">{cat.count}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {cat.items.map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-[#B4A5BD]">
                          <CheckCircleIcon className="w-3.5 h-3.5 text-[#FF3366]/50 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: terminal */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-[45%] w-full"
            >
              <TerminalBlock title="uiquarter init">
                <div className="space-y-1 text-[#B4A5BD] text-xs">
                  <div><span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter init</span></div>
                  <div className="mt-2">Discovered <span className="text-white">100 files</span> (23ms)</div>
                  <div>Running <span className="text-[#FF6B4D]">20 analyzers</span>...</div>
                  <div className="pl-4 mt-1">
                    <div className="flex justify-between"><span>ComponentAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>DependencyAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>StylingAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>UxAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>NextjsAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>AngularAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>ApiRouteAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="flex justify-between"><span>AuthAnalyzer</span><span className="text-green-400">done</span></div>
                    <div className="text-white/30">... 12 more</div>
                  </div>
                  <div className="mt-2">Built index: <span className="text-[#FF3366]">53 patterns</span>, <span className="text-[#FF6B4D]">10 edges</span></div>
                  <div>Generated <span className="text-white">5 insights</span></div>
                  <div className="mt-1 text-green-400">Done. <span className="text-[#B4A5BD]">(181ms)</span></div>
                </div>
              </TerminalBlock>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 6. MCP Server ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <div className="flex flex-col-reverse lg:flex-row gap-12 items-start">
            {/* Left: code */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-[45%] w-full space-y-4"
            >
              <TerminalBlock title="request.json">
                <pre className="text-xs text-[#B4A5BD] whitespace-pre">{`{
  "method": "tools/call",
  "params": {
    "name": "uiq_scope_context",
    "arguments": {
      "task": "refactor login form",
      "charBudget": "2000"
    }
  }
}`}</pre>
              </TerminalBlock>
              <TerminalBlock title="response">
                <div className="text-xs text-[#B4A5BD] space-y-1">
                  <div><span className="text-white">Components:</span> LoginForm, AuthProvider</div>
                  <div><span className="text-white">Deps:</span> LoginForm {'\u2192'} AuthProvider {'\u2192'} api/auth</div>
                  <div><span className="text-white">Patterns:</span> form-validation, error-boundary</div>
                  <div><span className="text-white">Insight:</span> <span className="text-[#FF6B4D]">hub-component</span> (AuthProvider)</div>
                  <div className="mt-2 text-white/30">~325 tokens</div>
                </div>
              </TerminalBlock>
            </motion.div>

            {/* Right: text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-[55%]"
            >
              <div className="flex items-center gap-3 mb-4">
                <ServerStackIcon className="w-7 h-7 text-[#FF3366]" />
                <h2 className="text-3xl font-bold">MCP Server</h2>
              </div>
              <p className="text-[#B4A5BD] mb-8 max-w-lg">
                Token-efficient AI integration with real-time query tools. Instead of loading massive context files, your AI assistant asks UIQuarter only what it needs.
              </p>
              <div className="space-y-4 mb-8">
                {mcpTools.map((tool) => (
                  <div key={tool.name} className="flex items-start gap-3">
                    <CommandLineIcon className="w-5 h-5 text-[#FF6B4D] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-mono text-sm text-white">{tool.name}</div>
                      <div className="text-xs text-[#B4A5BD] mt-0.5">{tool.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {['stdio transport', 'HTTP transport', 'JSON-RPC 2.0'].map((badge) => (
                  <span key={badge} className="px-3 py-1 rounded-full border border-white/10 bg-white/[0.02] text-xs text-[#B4A5BD]">
                    {badge}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </GradientBox>
      </section>

      {/* ── 7. Convention Enforcement ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ShieldCheckIcon className="w-7 h-7 text-[#FF3366]" />
              <h2 className="text-3xl font-bold">Convention Enforcement</h2>
            </div>
            <p className="text-[#B4A5BD] text-lg">10 rules. One config file. Zero drift.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: config */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="text-sm text-[#B4A5BD] mb-3 font-semibold">.uiqrc.json</div>
              <TerminalBlock title=".uiqrc.json">
                <pre className="text-xs text-[#B4A5BD] whitespace-pre">{`{
  "rules": {
    "file-naming": {
      "severity": "warning",
      "convention": "PascalCase"
    },
    "circular-deps": {
      "severity": "error"
    },
    "barrel-exports": {
      "severity": "info"
    },
    "min-accessibility": {
      "severity": "warning",
      "threshold": 0.5
    }
  }
}`}</pre>
              </TerminalBlock>
            </motion.div>

            {/* Right: lint output */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="text-sm text-[#B4A5BD] mb-3 font-semibold">uiquarter lint</div>
              <TerminalBlock title="terminal">
                <div className="text-xs space-y-2">
                  <div className="text-[#B4A5BD]">
                    <span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter lint</span>
                  </div>
                  <div className="text-[#B4A5BD]">Checking 10 rules across 25 files...</div>
                  <div className="mt-2">
                    <div className="flex gap-2">
                      <span className="text-yellow-400 font-semibold w-16">WARNING</span>
                      <span className="text-white">file-naming</span>
                    </div>
                    <div className="pl-[72px] text-[#B4A5BD]">utils/APIhelper.ts -- Expected PascalCase</div>
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <span className="text-red-400 font-semibold w-16">ERROR</span>
                      <span className="text-white">circular-deps</span>
                    </div>
                    <div className="pl-[72px] text-[#B4A5BD]">Button {'\u2192'} Card {'\u2192'} Button</div>
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <span className="text-yellow-400 font-semibold w-16">WARNING</span>
                      <span className="text-white">single-styling</span>
                    </div>
                    <div className="pl-[72px] text-[#B4A5BD]">Dashboard.tsx -- Mixed CSS modules + Tailwind</div>
                  </div>
                  <div className="mt-3 text-[#B4A5BD]">
                    <span className="text-white">3 violations</span> (1 error, 2 warnings)
                  </div>
                </div>
              </TerminalBlock>
            </motion.div>
          </div>

          {/* Rule pills */}
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-2 mt-10">
            {conventionRules.map((rule) => (
              <span key={rule} className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.02] text-xs font-mono text-[#B4A5BD] hover:border-[#FF3366]/30 hover:text-white transition-all">
                {rule}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 8. Drift Detection & CI ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ArrowsRightLeftIcon className="w-7 h-7 text-[#FF3366]" />
              <h2 className="text-3xl font-bold">Drift Detection & CI</h2>
            </div>
            <p className="text-[#B4A5BD] text-lg">Track architectural evolution. Catch regressions before merge.</p>
          </motion.div>

          {/* 3-panel grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {driftPanels.map((panel, i) => (
              <motion.div key={panel.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="text-sm text-[#B4A5BD] mb-3 font-semibold">{panel.title}</div>
                <TerminalBlock title="terminal">
                  <div className="text-xs space-y-1">
                    <div><span className="text-[#FF3366]">$</span> <span className="text-white">{panel.cmd}</span></div>
                    <div className="mt-2 space-y-1">
                      {panel.lines.map((line, j) => (
                        <div key={j} className="text-[#B4A5BD]">{line}</div>
                      ))}
                    </div>
                  </div>
                </TerminalBlock>
              </motion.div>
            ))}
          </div>

          {/* GitHub Actions YAML */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-sm text-[#B4A5BD] mb-3 font-semibold text-center">GitHub Actions -- ready to copy</div>
            <TerminalBlock title=".github/workflows/uiquarter.yml" className="max-w-2xl mx-auto">
              <pre className="text-xs text-[#B4A5BD] whitespace-pre">{`name: Architecture Check
on: [pull_request]
jobs:
  check:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
      - run: npm ci
      - run: npx uiquarter init
      - run: npx uiquarter ci --fail-on warning`}</pre>
            </TerminalBlock>
          </motion.div>
        </GradientBox>
      </section>

      {/* ── 9. HTML Reports ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* Left: dashboard mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="lg:w-[55%] w-full"
            >
              <div className="rounded-2xl border border-white/10 bg-[#0D0D12] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#0A0A0F]">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  <span className="ml-2 text-xs text-[#B4A5BD] font-mono">report.html</span>
                </div>
                <div className="p-5">
                  {/* Summary cards */}
                  <div className="grid grid-cols-4 gap-3 mb-5">
                    {[
                      { label: 'Components', val: '25' },
                      { label: 'Insights', val: '5' },
                      { label: 'Hub Nodes', val: '3' },
                      { label: 'Deep Chains', val: '1' },
                    ].map((s) => (
                      <div key={s.label} className="p-2.5 rounded-lg border border-white/5 bg-white/[0.02] text-center">
                        <div className="text-lg font-bold bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">{s.val}</div>
                        <div className="text-[10px] text-[#B4A5BD]">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Mini dep graph */}
                  <div className="p-4 rounded-lg border border-white/5 bg-white/[0.01] mb-5">
                    <div className="text-[10px] text-[#B4A5BD] mb-3 uppercase tracking-wider">Dependency Graph</div>
                    <div className="flex items-center justify-center gap-2 text-xs font-mono text-[#B4A5BD]">
                      <span className="px-2 py-1 rounded bg-[#FF3366]/20 text-[#FF3366] border border-[#FF3366]/30">App</span>
                      <span className="text-[#FF3366]">{'\u2192'}</span>
                      <span className="px-2 py-1 rounded bg-white/5 border border-white/10">Layout</span>
                      <span className="text-white/30">{'\u2192'}</span>
                      <span className="px-2 py-1 rounded bg-white/5 border border-white/10">Sidebar</span>
                      <span className="text-white/30">{'\u2192'}</span>
                      <span className="px-2 py-1 rounded bg-white/5 border border-white/10">NavItem</span>
                    </div>
                  </div>
                  {/* Mini table */}
                  <div className="rounded-lg border border-white/5 overflow-hidden">
                    <div className="grid grid-cols-4 gap-0 text-[10px] bg-white/[0.03] px-3 py-2 text-[#B4A5BD] uppercase tracking-wider">
                      <span>Component</span><span>Type</span><span>Deps</span><span>Dependents</span>
                    </div>
                    {[
                      ['App', 'Page', '3', '0'],
                      ['Navbar', 'Component', '2', '1'],
                      ['Footer', 'Component', '0', '1'],
                    ].map((row, i) => (
                      <div key={i} className={`grid grid-cols-4 gap-0 text-xs px-3 py-1.5 ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                        <span className="text-white">{row[0]}</span>
                        <span className="text-[#B4A5BD]">{row[1]}</span>
                        <span className="text-[#FF6B4D]">{row[2]}</span>
                        <span className="text-[#B4A5BD]">{row[3]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-[45%]"
            >
              <div className="flex items-center gap-3 mb-4">
                <ChartBarSquareIcon className="w-7 h-7 text-[#FF3366]" />
                <h2 className="text-3xl font-bold">HTML Reports</h2>
              </div>
              <p className="text-[#B4A5BD] mb-8 max-w-lg">
                Self-contained dashboards with zero server dependencies. Share architecture insights with your entire team -- designers, PMs, and engineers.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: PresentationChartLineIcon, text: 'Mermaid dependency graphs with hub highlighting' },
                  { icon: TableCellsIcon, text: 'Sortable component tables with search and filters' },
                  { icon: ChartBarIcon, text: 'Coverage bars for accessibility, error states, responsive' },
                  { icon: DocumentTextIcon, text: 'Single .html file -- share via email, no server needed' },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3">
                    <item.icon className="w-5 h-5 text-[#FF6B4D] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#B4A5BD]">{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-lg bg-[#0D0D12] border border-white/5 p-4 font-mono text-sm">
                <span className="text-[#FF3366]">$</span> <span className="text-white">uiquarter report --open</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 10. AI Targets ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Works with every AI coding tool</h2>
            <p className="text-[#B4A5BD] text-lg">One command generates optimized context for all major assistants.</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {aiTargets.map((target, i) => (
              <motion.div key={target.name} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-white/5 bg-white/[0.02] text-center hover:border-[#FF3366]/20 transition-all duration-300">
                {target.icon}
                <div className="text-sm font-semibold">{target.name}</div>
                <div className="text-xs text-[#B4A5BD] font-mono">{target.file}</div>
              </motion.div>
            ))}
          </div>
        </GradientBox>
      </section>

      {/* ── 11. CLI Commands ── */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">13 powerful commands</h2>
            <p className="text-[#B4A5BD] text-lg">Everything you need from a single CLI.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <TerminalBlock title="commands">
              <div className="space-y-3">
                {commands.map((c) => (
                  <div key={c.cmd} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <div className="flex-shrink-0">
                      <span className="text-[#FF3366]">$</span> <span className="text-white">{c.cmd}</span>
                    </div>
                    <div className="text-[#B4A5BD] text-xs sm:ml-auto">{c.desc}</div>
                  </div>
                ))}
              </div>
            </TerminalBlock>
          </motion.div>
        </div>
      </section>

      {/* ── 12. Framework Support ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Every major framework</h2>
            <p className="text-[#B4A5BD] text-lg max-w-2xl mx-auto">
              Dedicated analyzers for each framework. UIQuarter understands your stack's conventions, routing, and patterns out of the box.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {frameworks.map((fw, i) => (
              <motion.div key={fw.name} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}
                className="group flex flex-col items-center gap-3 p-5 rounded-xl border border-white/5 hover:border-white/20 transition-all duration-300">
                <img src={fw.logo} alt={fw.name} className="w-10 h-10 opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="text-sm font-semibold">{fw.name}</div>
                <div className="text-xs text-[#B4A5BD] text-center leading-relaxed">{fw.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 13. CTA ── */}
      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold mb-6">Ready to analyze your codebase?</h2>
            <div className="rounded-xl bg-[#0D0D12] border border-white/10 p-6 font-mono text-lg mb-8">
              <span className="text-[#FF3366]">$</span> <span className="text-white">npm install -g uiquarter</span>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://github.com/fuatkeles/uiquarter" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white font-semibold hover:opacity-90 transition-all">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                View on GitHub
              </a>
              <Link to="/docs"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl border border-white/10 text-white font-semibold hover:bg-white/5 transition-all">
                Read the docs <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
